import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Check, X, ArrowLeft, ArrowRight, Save, Award, Target, Clock, Zap, Star } from 'react-feather';
import { saveQuiz } from '../utils/quizStorage';
import styles from '../styles/quizDeck.module.css';

/**
 * Props: quiz object { title, questions:[{prompt,choices,answers:[idx]}] }
 */
const QuizDeck = ({ quiz }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [animationClass, setAnimationClass] = useState('');

  useEffect(() => {
    // Reset animation class after animation completes
    if (animationClass) {
      const timer = setTimeout(() => setAnimationClass(''), 500);
      return () => clearTimeout(timer);
    }
  }, [animationClass]);

  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return <div className={styles.deckWrapper}>Invalid quiz data</div>;
  }

  const question = quiz.questions[currentQuestion];
  const userAnswers = selectedAnswers[currentQuestion] || [];

  // Normalize correct answers into 0-based indices from various possible formats
  const normalizeCorrectIndices = (q) => {
    const ans = q?.answers ?? [];
    const choices = q?.choices ?? [];
    if (!Array.isArray(ans)) return [];

    // If numeric: detect 1-based vs 0-based
    if (ans.every(a => typeof a === 'number' && Number.isFinite(a))) {
      const max = Math.max(...ans);
      const min = Math.min(...ans);
      // Heuristic: if any answer equals choices.length, it's likely 1-based
      const isOneBased = min >= 1 && max === choices.length && !ans.includes(0);
      return ans.map(idx => {
        const normalized = isOneBased ? idx - 1 : idx;
        return Math.max(0,
          Math.min(choices.length - 1, normalized)
        );
      });
    }

    // If strings: support letters (A, B, C, ...), or exact/fuzzy text match
    const letterToIndex = (s) => {
      const m = String(s).trim().match(/^[A-Za-z]/);
      if (!m) return null;
      const code = m[0].toUpperCase().charCodeAt(0);
      const idx = code - 'A'.charCodeAt(0);
      return idx >= 0 && idx < choices.length ? idx : null;
    };

    const clean = (s) => String(s).trim().toLowerCase().replace(/^[-A-Da-d).\s]+/, '').replace(/\s+/g, ' ');

    return ans.map(a => {
      // Try letter mapping
      const byLetter = letterToIndex(a);
      if (byLetter !== null) return byLetter;
      // Try exact text match
      const target = clean(a);
      let idx = choices.findIndex(c => clean(c) === target);
      if (idx !== -1) return idx;
      // Try contains match
      idx = choices.findIndex(c => clean(c).includes(target) || target.includes(clean(c)));
      return idx !== -1 ? idx : -1;
    }).filter(i => i >= 0 && i < choices.length);
  };

  const normalizedCorrect = normalizeCorrectIndices(question);

  const handleAnswerChange = (choiceIndex) => {
    setSelectedAnswers(prev => {
      const current = prev[currentQuestion] || [];
      const newAnswers = current.includes(choiceIndex)
        ? current.filter(idx => idx !== choiceIndex)
        : [...current, choiceIndex];
      
      return {
        ...prev,
        [currentQuestion]: newAnswers
      };
    });
    
    // Show immediate feedback
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 2000);
  };

  const checkAnswer = () => {
    const correctAnswers = normalizedCorrect;
    const userAns = selectedAnswers[currentQuestion] || [];
    if (correctAnswers.length !== userAns.length) return false;
    return correctAnswers.every(answer => userAns.includes(answer));
  };

  const nextQuestion = () => {
    setAnimationClass(styles.slideOut);
    setTimeout(() => {
      if (currentQuestion < quiz.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setAnimationClass(styles.slideIn);
      } else {
        setShowResults(true);
      }
    }, 250);
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setAnimationClass(styles.slideOut);
      setTimeout(() => {
        setCurrentQuestion(currentQuestion - 1);
        setAnimationClass(styles.slideIn);
      }, 250);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    quiz.questions.forEach((q, idx) => {
      const correctAnswers = q.answers || [];
      const userAnswers = selectedAnswers[idx] || [];
      
      if (correctAnswers.length === userAnswers.length &&
          correctAnswers.every(answer => userAnswers.includes(answer))) {
        correct++;
      }
    });
    return correct;
  };

  const handleSave = () => {
    const quizData = {
      ...quiz,
      id: Date.now().toString(),
      savedAt: new Date().toISOString()
    };
    saveQuiz(quizData);
    setIsSaved(true);
  };

  if (showResults) {
    const score = calculateScore();
    const percentage = Math.round((score / quiz.questions.length) * 100);
    
    return (
      <div className={styles.deckWrapper}>
        <div className={`${styles.resultsCard} ${styles.fadeIn}`}>
          <div className={styles.resultsHeader}>
            <Award className={styles.trophyIcon} size={48} />
            <h3 className={styles.title}>Quiz Complete!</h3>
          </div>
          
          <div className={styles.scoreDisplay}>
            <div className={`${styles.scoreCircle} ${percentage >= 70 ? styles.good : styles.needsWork}`}>
              <span className={styles.scoreText}>{percentage}%</span>
            </div>
            <p className={styles.scoreDetails}>
              You got <strong>{score}</strong> out of <strong>{quiz.questions.length}</strong> questions correct
            </p>
          </div>
          
          <div className={styles.performanceMessage}>
            {percentage >= 90 ? (
              <span className={styles.excellent}>🎉 Excellent work!</span>
            ) : percentage >= 70 ? (
              <span className={styles.good}>👍 Good job!</span>
            ) : (
              <span className={styles.needsWork}>📚 Keep studying!</span>
            )}
          </div>
          
          <div className={styles.actions}>
            <button 
              className={styles.primaryBtn}
              onClick={() => {
                setShowResults(false);
                setCurrentQuestion(0);
                setSelectedAnswers({});
              }}
            >
              <Target size={16} />
              Retake Quiz
            </button>
            
            {!isSaved ? (
              <button className={styles.secondaryBtn} onClick={handleSave}>
                <Save size={16} />
                Save Quiz
              </button>
            ) : (
              <span className={styles.savedLabel}>
                <Check size={16} />
                Saved to Quizzes
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.deckWrapper}>
      <div className={styles.header}>
        <h3 className={styles.title}>{quiz.title || 'Interactive Quiz'}</h3>
        <div className={styles.progress}>
          <span className={styles.questionCounter}>
            Question {currentQuestion + 1} of {quiz.questions.length}
          </span>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className={`${styles.card} ${animationClass}`}>
        <div className={styles.prompt}>
          {question.prompt}
        </div>

        <ul className={styles.choiceList}>
          {question.choices.map((choice, idx) => {
            const isSelected = userAnswers.includes(idx);
            // const isCorrect = normalizedCorrect && normalizedCorrect.includes(idx);
            
            return (
              <li key={idx} className={styles.choiceItem}>
                <label className={`${styles.choiceLabel} ${isSelected ? styles.selected : ''}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleAnswerChange(idx)}
                    className={styles.choiceInput}
                  />
                  <span className={styles.choiceText}>{choice}</span>
                  {isSelected && (
                    <span className={styles.selectedIcon}>
                      <Check size={16} />
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>

        {showFeedback && userAnswers.length > 0 && (
          <div className={`${styles.feedback} ${styles.fadeIn}`}>
            {checkAnswer() ? (
              <span className={styles.correct}>
                <Check size={16} />
                Correct!
              </span>
            ) : (
              <span className={styles.incorrect}>
                <X size={16} />
                Try again
              </span>
            )}
            {!checkAnswer() && normalizedCorrect?.length > 0 && (
              <div className={styles.correctAnswerText}>
                Correct answer{normalizedCorrect.length > 1 ? 's' : ''}: {normalizedCorrect.map(i => question.choices[i]).join(', ')}
              </div>
            )}
          </div>
        )}

        <div className={styles.controls}>
          <button 
            className={styles.navBtn}
            onClick={prevQuestion}
            disabled={currentQuestion === 0}
          >
            <ArrowLeft size={16} />
            Previous
          </button>
          
          <div className={styles.centerInfo}>
            <span className={styles.selectionHint}>
              {userAnswers.length === 0 ? 'Select your answer(s)' : `${userAnswers.length} selected`}
            </span>
          </div>
          
          <button 
            className={`${styles.primaryBtn} ${userAnswers.length === 0 ? styles.disabled : ''}`}
            onClick={nextQuestion}
            disabled={userAnswers.length === 0}
          >
            {currentQuestion === quiz.questions.length - 1 ? 'Finish' : 'Next'}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizDeck;
